import { Chess, Move } from "chess.js";
import { Chessboard, INPUT_EVENT_TYPE } from "cm-chessboard";
import "cm-chessboard/assets/chessboard.css";
import "cm-chessboard/assets/extensions/markers/markers.css";
import "cm-chessboard/assets/extensions/promotion-dialog/promotion-dialog.css";
import {
  MARKER_TYPE,
  Markers,
} from "cm-chessboard/src/cm-chessboard/extensions/markers/Markers.js";
import { PromotionDialog } from "cm-chessboard/src/cm-chessboard/extensions/promotion-dialog/PromotionDialog.js";

import markerSvgURL from "url:cm-chessboard/assets/extensions/markers/markers.svg";
import chessboardSpriteURL from "url:cm-chessboard/assets/pieces/standard.svg";

import { VisionPosition } from "../../functions/lib/vision-position";
import * as api from "../scripts/api";
import { bindData, bindKey } from "../scripts/binding";
import { loading } from "../scripts/loading-spinner";
import { soundEffect } from "./sounds";
import { addScore, getInitialScore, loadScores } from "./scores";
import { loadAccuracy, updateAccuracy } from "./accuracy";
import { showBeacon } from "../scripts/beacons";
import { Position } from "../../functions/lib/json-api";
import {
  loadAchievements,
  processAchievements,
  removeNotification,
} from "./achievements";
import {
  incrementMoveStreak,
  loadMoveStreak,
  resetMoveStreak,
  saveMoveStreak,
} from "./streak";

const boardDiv = document.getElementById("board")!;
const $done = $(".js-done");
const $next = $(".js-next");
const $status = $("#status");
const skipIntroButton = document.getElementById("skip-intro")!;
const introNextButton = document.getElementById("intro-next")!;

const game = new Chess();

const board = new Chessboard(boardDiv, {
  assetsUrl: "/",
  style: {
    animationDuration: 200,
    pieces: {
      file: ".." + new URL(chessboardSpriteURL).pathname,
    },
  },
  extensions: [
    { class: PromotionDialog },
    {
      class: Markers,
      props: {
        autoMarkers: MARKER_TYPE.square,
        sprite: "../.." + new URL(markerSvgURL).pathname,
      },
    },
  ],
});

const inCheckMarker = { class: "in-check", slice: "markerSquare" };
const opponentMoveMarker = {
  class: "opponent-highlight",
  slice: "markerSquare",
};

loadAccuracy();
loadScores();
loadAchievements();
loadMoveStreak();

// Show the sign up prompt if the user is not logged in
api.getCurrentUser().then((user) => {
  if (!user) {
    document.querySelectorAll<HTMLElement>("[data-ref='logged-out']").forEach(
      (el) => {
        el.hidden = false;
      },
    );
  }
});

// Puzzle state
let isIntro = false;
let currentPositionId: string | null = null;
let puzzleAttempted = 0;
let startTime: number | null = null;
let checks: Move[] = [];
let captures: Move[] = [];
let moves: Move[] = [];
let pseudoMoves: Move[] = []; // Moves that are not legal because of check
let targets: Move[] = [];
let positionScore = 0;
let movesRequest: ReturnType<typeof api.submitMoves> | null = null;
const found = new Set<Move>();

board.enableMoveInput((event) => {
  switch (event.type) {
    case INPUT_EVENT_TYPE.moveInputStarted:
      return game.turn() === event.piece.at(0);

    case INPUT_EVENT_TYPE.validateMoveInput:
      if (!event.promotion && isPromotionMove(event)) {
        handlePromotion(event);
        return true; // Show the pawn on the promotion square while we wait for the user to choose a piece
      }
      validateMoveInput(event.squareFrom, event.squareTo, event.promotion);
      return false;
  }
});

function isPromotionMove(event: any) {
  return pseudoMoves.some((m) =>
    m.from === event.squareFrom && m.to === event.squareTo && m.promotion
  );
}

function handlePromotion(event: any) {
  board.showPromotionDialog(event.squareTo, game.turn(), (result) => {
    if (result?.piece) {
      validateMoveInput(
        event.squareFrom,
        event.squareTo,
        result.piece[1].toLowerCase(),
      );
      board.setPiece(result.square, result.piece, true);
    }
    board.setPosition(game.fen());
  });
}

function validateMoveInput(
  source: string,
  target: string,
  promotion: string | undefined,
) {
  $done.trigger("focus");

  // see if the move is in the list of possible moves
  const move = moves.find((m) =>
    m.from === source && m.to === target && m.promotion === promotion
  );
  if (move) {
    assessMove(move);
  } else if (pseudoMoves.find((m) => m.from === source && m.to === target)) {
    // The move is illegal, so the king is attacked
    // Flash the in-check marker 3 times to indicate this
    const kingSq = getKingSquare();
    let count = 4;
    const intervalId = setInterval(() => {
      board.getMarkers(inCheckMarker, kingSq).length === 0
        ? board.addMarker(inCheckMarker, kingSq)
        : board.removeMarkers(undefined, kingSq);
      if (--count === 0) clearInterval(intervalId);
    }, 100);
  }
}

function getKingSquare() {
  return game.board().flat().find((p) =>
    p?.type === "k" && p?.color === game.turn()
  )?.square;
}

function assessMove(move: Move) {
  const marker = { slice: "markerSquare" } as { class?: string; slice: string };
  if (targets.includes(move)) {
    marker.class = "correct";
    soundEffect("move");
    if (!found.has(move) && !isIntro) {
      incrementMoveStreak();
    }
    found.add(move);
    bindKey("found-moves", Array.from(found).map((m) => m.san).join(", "));
    if (puzzleAttempted > 1) {
      bindKey(
        "missed-moves",
        targets.filter((c) => !found.has(c)).map((m) => m.san).join(", "),
      );
    }
  } else {
    marker.class = "incorrect";
  }
  board.addMarker(marker, move.to);
  setTimeout(() => board.removeMarkers(marker, move.to), 500);
  if (isIntro && found.size === targets.length) {
    setTimeout(() => {
      showBeacon("vision-done-button");
    }, 1000);
  }
}

function onDone() {
  if (!startTime || !currentPositionId) return;

  // Work out rate based on: how many moves there were and how many pieces on the board
  // Time = search time + move time
  // Move time = time / moves
  // Search time = time / candidates
  const time = (performance.now() - startTime) / 1000;
  const pieces = game.board().flat().filter((p) => p?.color === game.turn());

  const missed = targets.filter((c) => !found.has(c)).map((m) => m.san);
  const moveType = targets === checks ? "check" : "capture";
  const inflectedMoveType = targets.length == 1 ? moveType : moveType + "s";

  // Only record accuracy on the first attempt for each puzzle
  if (!puzzleAttempted) {
    positionScore = pieces.length + found.size; // Set the score only on the first attempt
    if (!isIntro) {
      movesRequest = api.submitMoves(
        currentPositionId,
        moveType,
        game.fen(),
        Array.from(found),
        time,
      );
      const { count: positionCount } = updateAccuracy(
        found.size,
        targets.length,
        pieces.length,
        time,
      );
      if (positionCount == 10) showBeacon("accuracy");
      saveMoveStreak(); // Because we don't want to do this after every move
    }
  }

  // Update UI
  if (missed.length > 0) {
    soundEffect("missed");
    const accuracy = found.size / targets.length;
    let admonition = "";
    switch (true) {
      case accuracy > 0.9:
        admonition = "Good effort!";
        break;
      case accuracy > 0.75 || missed.length == 1:
        admonition = "Almost there!";
        break;
      case accuracy > 0.5 || missed.length == 2:
        admonition = "Nearly!";
        break;
      default:
        admonition = "Oops!";
    }
    setStatus(
      `${admonition} You found ${found.size} out of ${targets.length} ${inflectedMoveType}. Keep trying.`,
      "warning",
    );
    resetMoveStreak();
    if (puzzleAttempted > 0) {
      bindKey("missed-moves", missed.join(", "));
    }
  } else {
    soundEffect("success");
    addScore(positionScore);
    movesRequest?.then((r) => r && processAchievements(r.achievements));
    let congrats;
    switch (targets.length) {
      case 0:
        congrats = `Smart! There were no ${inflectedMoveType} to find.`;
        break;
      case 1:
        congrats = `Nice! You found the only ${inflectedMoveType}.`;
        break;
      case 2:
        congrats = `Great! You found both ${inflectedMoveType}.`;
        break;
      default:
        congrats =
          `Perfect! You found all ${targets.length} ${inflectedMoveType}.`;
    }
    const verb = targets.length == 1 ? "was" : "were";
    if (puzzleAttempted == 1) {
      congrats =
        `Nice spotting! There ${verb} ${targets.length} ${inflectedMoveType}.`;
    } else if (puzzleAttempted > 1) {
      congrats =
        `We got there! There ${verb} ${targets.length} ${inflectedMoveType}.`;
    }
    setStatusIcon("success");
    setStatus(congrats);
    $done.hide();
    if (isIntro) {
      introNextButton.classList.remove("is-hidden");
    }
    $next.trigger("focus");
    boardDiv.classList.add("disabled");
    if (targets == captures && getInitialScore() == 0 && !isIntro) {
      showBeacon("scores");
    }
  }
  puzzleAttempted += 1;
}

function onNext() {
  // Hide notification
  removeNotification();
  if (targets === checks) {
    puzzleAttempted = 0;
    startTime = performance.now();
    targets = captures;
    found.clear();
    bindData({ "found-moves": "", "missed-moves": "" });
    const colour = game.turn() === "w" ? "white" : "black";
    setStatusIcon(colour);
    setStatus(`Find any <b>captures</b> that <b>${colour}</b> can play.`);
    boardDiv.classList.remove("disabled");
  } else {
    loadPosition();
  }
  $done.show();
  $done.trigger("focus");
}

async function loadPosition() {
  // Update UI
  setStatus(`${loading.outerHTML} Loading position…`);

  // Fetch puzzle and update game state
  const position = await api.getRandomPosition();
  if (!position) {
    setStatus(`Failed to load puzzle. Try again soon.`, "danger");
    return;
  }
  setupGame(position);
  boardDiv.classList.remove("disabled");
  setStatusIcon(game.turn() === "w" ? "white" : "black");
  setStatus(
    `Find any <b>checks</b> that <b>${
      game.turn() == "w" ? "White" : "Black"
    }</b> can play.`,
  );
  $done.trigger("focus");
}

function setupGame(position: Position) {
  const fromSq = position.move.slice(0, 2),
    toSq = position.move.slice(2, 4);

  puzzleAttempted = 0;
  currentPositionId = position.id;
  game.load(position.fen);
  game.move(position.move, { sloppy: true });

  const visionPosition = new VisionPosition(game.fen());

  pseudoMoves = game.moves({ verbose: true, legal: false });
  moves = visionPosition.moves;
  checks = visionPosition.checks;
  captures = visionPosition.captures;
  startTime = performance.now();
  targets = checks;
  found.clear();
  bindData({ "found-moves": "", "missed-moves": "" });

  // Update UI
  board.removeMarkers();
  board.setPosition(position.fen, false);
  board.addMarker(
    opponentMoveMarker,
    fromSq,
  );
  board.addMarker(opponentMoveMarker, toSq);
  if (game.in_check()) board.addMarker(inCheckMarker, getKingSquare());
  board.setPosition(game.fen(), true);
}

$done.on("click", onDone);
$next.on("click", onNext);
introNextButton.addEventListener("click", onIntroNext);
skipIntroButton.addEventListener("click", finishIntro);

boardDiv.classList.add("disabled");
api.getCurrentUser().then((user) => {
  if (user || localStorage.getItem("finished-intro") === "true") {
    startTraining();
  } else {
    loadIntro();
  }
});

function startTraining() {
  $done.show();
  $done.removeClass("is-hidden");
  $next.removeClass("is-hidden");
  loadPosition();
}

function loadIntro() {
  isIntro = true;
  skipIntroButton.classList.remove("is-hidden");
  introNextButton.classList.remove("is-hidden");
  setStatus(
    "Welcome! We're going to train your brain to quickly see opportunities and threats on the board.",
  );
  setTimeout(() => {
    showBeacon("vision-next-button");
  }, 7000);
}

let introStep = 1;
function onIntroNext() {
  switch (introStep) {
    case 1:
      introNextButton.classList.add("is-hidden");
      $done.removeClass("is-hidden");
      setupGame({
        id: "lStA8",
        fen: "r1bq3r/pp1kbBpp/2np4/3Np3/Q7/8/PP3PPP/R1B2RK1 b - - 5 15",
        move: "h8f8",
      });
      boardDiv.classList.remove("disabled");
      setStatusIcon("white");
      setStatus(`
        Firstly, we'll look for opportunities.<br>
        Find all the <b>checks</b> that <b>white</b> can play from this position,
        then press <i>Done</i>.
      `);
      break;
    case 2:
      onNext();
      setStatus("Now find any <b>captures</b> that <b>white</b> can play.");
      break;
    case 3:
      introNextButton.classList.add("is-hidden");
      $done.show();
      setupGame({
        id: "intro2",
        fen: "1k6/pp2R3/2q5/8/1B3n2/1P6/P4PPP/6K1 w - - 0 1",
        move: "g2g3",
      });
      boardDiv.classList.remove("disabled");
      setStatusIcon("black");
      setStatus(
        "In this position, we'll look for threats.<br>Find any <b>checks</b> that <b>black</b> can play.",
      );
      break;
    case 4:
      onNext();
      setStatus(`
        Sometimes there won't be any moves to find!
        If you don't see any, just press <i>Done</i>.<br>
        Find any <b>captures</b> that <b>black</b> can play.
      `);
      break;
    case 5:
      setStatusIcon("black");
      setStatus(
        "Try to be both accurate and fast.<br>Now see if you can find 30 moves in a row!",
      );
      break;
    case 6:
      finishIntro();
  }
  introStep++;
}

function finishIntro() {
  localStorage.setItem("finished-intro", "true");
  isIntro = false;
  skipIntroButton.classList.add("is-hidden");
  introNextButton.classList.add("is-hidden");
  startTraining();
}

// Add a listener for the 'keydown' event.
document.addEventListener("keydown", function (event) {
  // If the key pressed is the 'Enter' key, call the 'onDone' function.
  if (event.key === "Enter") {
    onDone();
  }
});

// Add a click event on various child elements to close the parent modal
(document.querySelectorAll(
  ".modal-background, .modal-close, .modal-card-head .delete, .modal-card-foot .button",
) || []).forEach(($close) => {
  const $target = $close.closest(".modal");

  $close.addEventListener("click", () => {
    if ($target) closeModal($target);
  });
});

// Show the modal when the user clicks the link with a data-target of 'modal'
document.querySelectorAll<HTMLAnchorElement>("a[data-target='modal']")
  .forEach((el) => {
    el.onclick = (e) => {
      e.preventDefault();
      const target = el.getAttribute("href")!;
      $(target).toggleClass("is-active");
    };
  });

// Add a keyboard event to close all modals
document.addEventListener("keydown", (event) => {
  const e = event || window.event;

  if (e.keyCode === 27) { // Escape key
    closeAllModals();
  }
});

function closeModal($el: Element) {
  $el.classList.remove("is-active");
}

function closeAllModals() {
  (document.querySelectorAll(".modal") || []).forEach(($modal) => {
    closeModal($modal);
  });
}

function setStatus(
  message: string,
  status: "success" | "warning" | "danger" | "link" = "link",
) {
  $status.removeClass("is-success is-warning is-danger is-link");
  $status.addClass(`is-${status}`);
  bindKey("status", message);
}

function setStatusIcon(icon: string) {
  $status.find("[data-status]").hide();
  $status.find(`[data-status="${icon}"]`).show();
}

// Replace our placeholder link with the real one
document.querySelectorAll("[data-href='stripe-payment-link']").forEach((el) => {
  el.setAttribute("href", process.env["STRIPE_PAYMENT_URL"]!);
});
